import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { APIClient } from 'helpers/api_helper';

const api_helper = new APIClient();

// Async thunks
export const detectAnomalies = createAsyncThunk(
    'anomalyDetection/detectAnomalies',
    async ({ employeeId, anomalyTypes, days }: {
        employeeId: string;
        anomalyTypes: string[];
        days: number;
    }) => {
        const response = await api_helper.create(`/api/anomaly-detection/detect/${employeeId}`, {
            anomaly_types: anomalyTypes,
            days: days
        });
        return response.data || response;
    }
);

export const analyzePatterns = createAsyncThunk(
    'anomalyDetection/analyzePatterns',
    async ({ employeeId, period }: { employeeId: string; period: string }) => {
        const response = await api_helper.get(`/api/anomaly-detection/patterns/${employeeId}?period=${period}`);
        return response.data || response;
    }
);

export const getAnomalyDashboard = createAsyncThunk(
    'anomalyDetection/getDashboard',
    async (days: number = 30) => {
        const response = await api_helper.get(`/api/anomaly-detection/dashboard?days=${days}`);
        const result = response.data || response;
        return result;
    }
);

export const getAnomalyAlerts = createAsyncThunk(
    'anomalyDetection/getAlerts',
    async () => {
        const response = await api_helper.get('/api/anomaly-detection/alerts');
        const result = response.data || response;
        return result;
    }
);

export const getAllAnomalies = createAsyncThunk(
    'anomalyDetection/getAllAnomalies',
    async (params: { status?: string; severity?: string; anomaly_type?: string; days?: number }) => {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value) queryParams.append(key, value.toString());
        });
        
        const response = await api_helper.get(`/api/anomaly-detection/anomalies?${queryParams.toString()}`);
        const result = response.data || response;
        return result;
    }
);

export const updateAnomalyStatus = createAsyncThunk(
    'anomalyDetection/updateAnomalyStatus',
    async ({ anomalyId, status, resolution_notes, assigned_to }: {
        anomalyId: string;
        status?: string;
        resolution_notes?: string;
        assigned_to?: string;
    }) => {
        const response = await api_helper.put(`/api/anomaly-detection/anomalies/${anomalyId}`, {
            status,
            resolution_notes,
            assigned_to
        });
        return response.data || response;
    }
);

export const getEmployeeAnomalySummary = createAsyncThunk(
    'anomalyDetection/getEmployeeSummary',
    async (employeeId: string) => {
        const response = await api_helper.get(`/api/anomaly-detection/employee/${employeeId}/summary`);
        return response.data || response;
    }
);

// Slice
const anomalyDetectionSlice = createSlice({
    name: 'anomalyDetection',
    initialState: {
        anomalies: [] as any[],
        patterns: {} as any,
        dashboard: {} as any,
        alerts: [] as any[],
        employeeSummary: {} as any,
        loading: false,
        error: null as string | null
    },
    reducers: {
        clearAnomalies: (state) => {
            state.anomalies = [];
            state.error = null;
        },
        clearPatterns: (state) => {
            state.patterns = {};
            state.error = null;
        },
        clearEmployeeSummary: (state) => {
            state.employeeSummary = {};
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Detect Anomalies
            .addCase(detectAnomalies.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(detectAnomalies.fulfilled, (state, action) => {
                state.loading = false;
                state.anomalies = action.payload?.data || [];
            })
            .addCase(detectAnomalies.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'An error occurred';
            })
            
            // Analyze Patterns
            .addCase(analyzePatterns.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(analyzePatterns.fulfilled, (state, action) => {
                state.loading = false;
                state.patterns = action.payload?.data || {};
            })
            .addCase(analyzePatterns.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'An error occurred';
            })
            
            // Get Dashboard
            .addCase(getAnomalyDashboard.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getAnomalyDashboard.fulfilled, (state, action) => {
                state.loading = false;
                // Try different possible data structures
                const dashboardData = action.payload?.data || action.payload || {};
                state.dashboard = dashboardData;
            })
            .addCase(getAnomalyDashboard.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'An error occurred';
            })
            
            // Get Alerts
            .addCase(getAnomalyAlerts.fulfilled, (state, action) => {
                // Try different possible data structures
                const alertsData = action.payload?.data || action.payload || [];
                state.alerts = alertsData;
            })
            .addCase(getAnomalyAlerts.rejected, (state, action) => {
                state.error = action.error.message || 'An error occurred';
            })
            
            // Get All Anomalies
            .addCase(getAllAnomalies.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getAllAnomalies.fulfilled, (state, action) => {
                state.loading = false;
                // Try different possible data structures
                const anomaliesData = action.payload?.data || action.payload || [];
                state.anomalies = anomaliesData;
            })
            .addCase(getAllAnomalies.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'An error occurred';
            })
            
            // Update Anomaly Status
            .addCase(updateAnomalyStatus.fulfilled, (state, action) => {
                // Update the specific anomaly in the list
                const updatedAnomaly = action.payload?.data;
                if (updatedAnomaly) {
                    const index = state.anomalies.findIndex((a: any) => a.id === updatedAnomaly.id);
                    if (index !== -1) {
                        state.anomalies[index] = updatedAnomaly;
                    }
                }
            })
            .addCase(updateAnomalyStatus.rejected, (state, action) => {
                state.error = action.error.message || 'An error occurred';
            })
            
            // Get Employee Summary
            .addCase(getEmployeeAnomalySummary.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getEmployeeAnomalySummary.fulfilled, (state, action) => {
                state.loading = false;
                state.employeeSummary = action.payload?.data || {};
            })
            .addCase(getEmployeeAnomalySummary.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'An error occurred';
            });
    }
});

export const { clearAnomalies, clearPatterns, clearEmployeeSummary } = anomalyDetectionSlice.actions;
export default anomalyDetectionSlice.reducer;
